import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Bell,
  Check,
  MessageSquare,
  ExternalLink,
  Filter,
  CheckCircle,
  AlertTriangle,
  Clock,
  ChevronRight,
  ChevronLeft,
  Bookmark,
  CalendarIcon,
} from "lucide-react";

const AlertsPage = () => {
  const navigate = useNavigate();
  const { authAxios, user } = useAuth();
  const isAgent = user?.role === "agent";
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedAlert, setSelectedAlert] = useState(null);
  const [commentText, setCommentText] = useState("");
  const [followUpStatus, setFollowUpStatus] = useState("pendiente");
  const [saving, setSaving] = useState(false);
  const [filterType, setFilterType] = useState("all"); // all, risk_alert, low_sentiment

  // Pagination & Date Filters
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [dateRange, setDateRange] = useState({
    from: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000),
    to: new Date(),
  });

  const fetchAlerts = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: page.toString(),
        limit: "25",
      });
      if (dateRange.from) {
        params.append("date_from", format(dateRange.from, "yyyy-MM-dd"));
      }
      if (dateRange.to) {
        params.append("date_to", format(dateRange.to, "yyyy-MM-dd"));
      }
      const res = await authAxios.get(`/notifications?${params}`);
      setAlerts(res.data.notifications || []);
      setTotalPages(res.data.pages || 1);
      setTotal(res.data.total || 0);
    } catch (e) {
      console.error(e);
      toast.error("Error al cargar alertas");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAlerts();
  }, [page, dateRange]);

  const handleSelectAlert = (alert) => {
    setSelectedAlert(alert);
    setCommentText(alert.comment || "");
    setFollowUpStatus(alert.status || "pendiente");
  };

  const handleSaveComment = async () => {
    if (!selectedAlert) return;
    setSaving(true);
    try {
      await authAxios.post(`/notifications/${selectedAlert.id}/comment`, {
        comment: commentText,
        status: followUpStatus
      });
      toast.success("Seguimiento guardado correctamente");
      // Update local state
      setAlerts(alerts.map(a => a.id === selectedAlert.id ? {
        ...a,
        comment: commentText,
        status: followUpStatus,
        is_read: true
      } : a));
      setSelectedAlert(null);
    } catch (e) {
      toast.error("Error al guardar comentario");
    } finally {
      setSaving(false);
    }
  };

  const handleMarkRead = async (alertId, e) => {
    if (e) e.stopPropagation();
    try {
      await authAxios.post(`/notifications/${alertId}/read`);
      setAlerts(alerts.map(a => a.id === alertId ? { ...a, is_read: true } : a));
      toast.success("Alerta marcada como leída");
    } catch (e) {
      toast.error("Error al marcar como leída");
    }
  };

  const filteredAlerts = alerts.filter(a => {
    if (filterType === "all") return true;
    return a.type === filterType;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  const getPriorityBadge = (priority) => {
    switch (priority) {
      case "critical":
        return <Badge className="bg-rose-100 text-rose-700 hover:bg-rose-100 uppercase text-[10px]">Crítico</Badge>;
      case "high":
        return <Badge className="bg-orange-100 text-orange-700 hover:bg-orange-100 uppercase text-[10px]">Alto</Badge>;
      default:
        return <Badge className="bg-sky-100 text-sky-700 hover:bg-sky-100 uppercase text-[10px]">Medio</Badge>;
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "resuelto":
        return <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100 text-[10px]">Resuelto</Badge>;
      case "en_seguimiento":
        return <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100 text-[10px]">En Seguimiento</Badge>;
      default:
        return <Badge className="bg-slate-100 text-slate-600 hover:bg-slate-100 text-[10px]">Pendiente</Badge>;
    }
  };

  return (
    <div className="space-y-8 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 bg-white p-6 rounded-2xl border border-slate-100 shadow-sm">
        <div>
          <h1 className="text-3xl font-extrabold text-slate-950 flex items-center gap-3">
            <Bell className="w-8 h-8 text-purple-600 animate-pulse" />
            <span>Alertas de Riesgo y Seguimiento</span>
          </h1>
          <p className="text-slate-500 mt-1 text-sm">
            Audita, marca como leídas y añade comentarios de seguimiento a las llamadas que presentaron señales críticas o bajo sentimiento.
          </p>
        </div>

        <div className="flex items-center gap-3">
          <div className="hidden sm:flex items-center gap-1 bg-slate-100 p-0.5 rounded-lg border border-slate-200">
            <Button
              variant="ghost"
              size="sm"
              className="text-xs h-7 px-2 hover:bg-white hover:shadow-xs text-slate-600 font-medium"
              onClick={() => setDateRange({ from: new Date(), to: new Date() })}
            >
              Hoy
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="text-xs h-7 px-2 hover:bg-white hover:shadow-xs text-slate-600 font-medium"
              onClick={() => {
                const now = new Date();
                const startOfWeek = new Date(now.setDate(now.getDate() - now.getDay()));
                setDateRange({ from: startOfWeek, to: new Date() });
              }}
            >
              Semana
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="text-xs h-7 px-2 hover:bg-white hover:shadow-xs text-slate-600 font-medium"
              onClick={() => {
                const now = new Date();
                const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
                setDateRange({ from: startOfMonth, to: new Date() });
              }}
            >
              Mes
            </Button>
          </div>

          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" className="flex items-center gap-2 bg-slate-50 border-slate-200 text-slate-700">
                <CalendarIcon className="w-4 h-4 text-slate-400" />
                <span>
                  {dateRange.from ? format(dateRange.from, "dd MMM", { locale: es }) : ""} -{" "}
                  {dateRange.to ? format(dateRange.to, "dd MMM", { locale: es }) : ""}
                </span>
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="end">
              <Calendar
                mode="range"
                selected={dateRange}
                onSelect={(range) => range && setDateRange(range)}
                numberOfMonths={2}
                locale={es}
              />
            </PopoverContent>
          </Popover>

          <Select value={filterType} onValueChange={setFilterType}>
            <SelectTrigger className="w-[180px] bg-slate-50 border-slate-200">
              <Filter className="w-4 h-4 text-slate-400 mr-2" />
              <SelectValue placeholder="Filtrar tipo" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas las alertas</SelectItem>
              <SelectItem value="risk_alert">Alertas de Riesgo</SelectItem>
              <SelectItem value="low_sentiment">Bajo Sentimiento</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        
        {/* Alerts List */}
        <div className="lg:col-span-2 space-y-4">
          {filteredAlerts.length > 0 ? (
            <>
              {filteredAlerts.map(alert => (
                <Card 
                  key={alert.id} 
                  onClick={() => handleSelectAlert(alert)}
                  className={`cursor-pointer hover:shadow-md transition-all duration-300 border-l-4 ${
                    alert.is_read ? 'border-l-slate-200 opacity-80' : 'border-l-purple-500 bg-purple-50/5'
                  } ${selectedAlert?.id === alert.id ? 'ring-2 ring-purple-500' : ''}`}
                >
                  <CardContent className="p-5">
                    <div className="flex items-start justify-between gap-4">
                      <div className="space-y-2 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          {getPriorityBadge(alert.priority)}
                          {getStatusBadge(alert.status || "pendiente")}
                          {!alert.is_read && (
                            <Badge className="bg-purple-100 text-purple-700 hover:bg-purple-100 text-[10px]">Nuevo</Badge>
                          )}
                          <span className="text-xs text-slate-400 flex items-center gap-1">
                            <Clock className="w-3.5 h-3.5" />
                            {alert.created_at ? format(new Date(alert.created_at), "dd MMM yyyy, HH:mm", { locale: es }) : ""}
                          </span>
                        </div>
                        
                        <h3 className="font-bold text-slate-900 text-base">{alert.title}</h3>
                        <p className="text-sm text-slate-600 line-clamp-2">{alert.message}</p>
                        
                        {alert.comment && (
                          <div className="mt-3 bg-slate-50 border border-slate-100 p-2.5 rounded-lg text-xs text-slate-500 flex items-start gap-2">
                            <MessageSquare className="w-4 h-4 text-purple-500 shrink-0 mt-0.5" />
                            <div>
                              <strong className="text-slate-700 font-semibold block mb-0.5">Seguimiento:</strong>
                              <p className="italic">{alert.comment}</p>
                            </div>
                          </div>
                        )}
                      </div>
                      
                      <div className="flex flex-col items-end gap-3 shrink-0">
                        {!alert.is_read && (
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            className="h-8 w-8 p-0 text-slate-400 hover:text-purple-600 hover:bg-purple-50 rounded-full"
                            onClick={(e) => handleMarkRead(alert.id, e)}
                            title="Marcar como leída"
                          >
                            <Check className="w-4 h-4" />
                          </Button>
                        )}
                        <ChevronRight className="w-5 h-5 text-slate-300" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}

              {/* Pagination controls */}
              <div className="flex items-center justify-between px-4 py-3 bg-white border border-slate-100 rounded-xl shadow-sm mt-6">
                <p className="text-sm text-slate-500">
                  Mostrando {((page - 1) * 25) + 1} - {Math.min(page * 25, total)} de {total}
                </p>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page === 1}
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <span className="text-sm text-slate-600">
                    Página {page} de {totalPages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                  >
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </>
          ) : (
            <Card className="p-8 text-center text-slate-400 text-sm">
              <Bell className="w-12 h-12 text-slate-200 mx-auto mb-3" />
              <span>No se encontraron alertas en la bandeja.</span>
            </Card>
          )}
        </div>

        {/* Action Panel Drawer */}
        <div className="space-y-6">
          {selectedAlert ? (
            <Card className="sticky top-6 border border-slate-200 shadow-lg animate-slide-in">
              <CardHeader className="bg-slate-50 border-b border-slate-100 p-5 rounded-t-2xl flex flex-row items-center justify-between">
                <CardTitle className="text-lg font-bold text-slate-800 flex items-center gap-2">
                  <Bookmark className="w-5 h-5 text-purple-600" />
                  <span>Detalle de Seguimiento</span>
                </CardTitle>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="text-xs text-slate-500 hover:text-slate-800"
                  onClick={() => setSelectedAlert(null)}
                >
                  Cerrar
                </Button>
              </CardHeader>
              <CardContent className="p-6 space-y-6">
                
                {/* Alert details */}
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    {getPriorityBadge(selectedAlert.priority)}
                    {getStatusBadge(selectedAlert.status || "pendiente")}
                  </div>
                  <h3 className="font-bold text-slate-950 text-lg leading-tight">{selectedAlert.title}</h3>
                  <p className="text-sm text-slate-600 leading-relaxed bg-slate-50 p-4 rounded-xl border border-slate-100">
                    {selectedAlert.message}
                  </p>
                </div>

                {/* Direct link to Call */}
                {selectedAlert.call_id && (
                  <Button 
                    onClick={() => navigate(`/calls/${selectedAlert.call_id}`)}
                    className="w-full bg-sky-600 hover:bg-sky-700 text-white font-semibold flex items-center justify-center gap-2 shadow-sm"
                  >
                    <ExternalLink className="w-4 h-4" />
                    <span>Ver Detalles de la Llamada</span>
                  </Button>
                )}

                <hr className="border-slate-100" />

                {/* Follow up Form or Read-only Feedback */}
                {isAgent ? (
                  <div className="space-y-4 bg-purple-50/50 p-4 rounded-xl border border-purple-100">
                    <h4 className="text-sm font-bold text-purple-900 flex items-center gap-2">
                      <MessageSquare className="w-4 h-4 text-purple-600" />
                      Retroalimentación del Supervisor
                    </h4>
                    
                    <div className="space-y-1">
                      <p className="text-xs font-semibold text-slate-500">Estado del Seguimiento</p>
                      <div>{getStatusBadge(selectedAlert.status || "pendiente")}</div>
                    </div>

                    {selectedAlert.comment ? (
                      <div className="space-y-1">
                        <p className="text-xs font-semibold text-slate-500">Comentarios del Supervisor</p>
                        <p className="text-sm text-slate-700 italic bg-white p-3 rounded-lg border border-purple-100">
                          "{selectedAlert.comment}"
                        </p>
                      </div>
                    ) : (
                      <p className="text-xs text-slate-400 italic">No hay comentarios registrados por el supervisor todavía.</p>
                    )}
                  </div>
                ) : (
                  <div className="space-y-4">
                    <h4 className="text-sm font-bold text-slate-800">Actualizar Estado y Comentarios</h4>
                    
                    <div className="space-y-2">
                      <label className="text-xs font-semibold text-slate-500 block">Estado del Seguimiento</label>
                      <Select value={followUpStatus} onValueChange={setFollowUpStatus}>
                        <SelectTrigger className="w-full border-slate-200">
                          <SelectValue placeholder="Seleccionar Estado" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="pendiente">Pendiente (Sin seguimiento)</SelectItem>
                          <SelectItem value="en_seguimiento">En Seguimiento (Auditoría activa)</SelectItem>
                          <SelectItem value="resuelto">Resuelto (Agente retroalimentado)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <label className="text-xs font-semibold text-slate-500 block">Comentarios / Bitácora</label>
                      <Textarea
                        placeholder="Escribe aquí las acciones tomadas para resolver esta alerta..."
                        value={commentText}
                        onChange={(e) => setCommentText(e.target.value)}
                        className="min-h-[120px] text-sm border-slate-200 focus-visible:ring-purple-500"
                      />
                    </div>

                    <Button 
                      onClick={handleSaveComment}
                      disabled={saving}
                      className="w-full bg-purple-600 hover:bg-purple-700 text-white font-bold tracking-wide"
                    >
                      {saving ? "Guardando..." : "Guardar Seguimiento"}
                    </Button>
                  </div>
                )}

              </CardContent>
            </Card>
          ) : (
            <Card className="sticky top-6 bg-slate-50 border border-slate-200/60 p-6 rounded-2xl flex flex-col items-center justify-center text-center h-[350px]">
              <div className="w-14 h-14 bg-purple-50 rounded-full flex items-center justify-center mb-4 text-purple-600">
                <Bell className="w-7 h-7" />
              </div>
              <h3 className="font-bold text-slate-800 text-base">Auditoría de Alertas</h3>
              <p className="text-xs text-slate-500 mt-2 max-w-[200px] leading-relaxed">
                Selecciona cualquier alerta de la bandeja de entrada para registrar comentarios de seguimiento o retroalimentación.
              </p>
            </Card>
          )}
        </div>

      </div>
    </div>
  );
};

export default AlertsPage;
